"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "@/lib/utils";
import SvgAnglesDown from "@/styles/icons/AnglesDown";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn("border-b border-accent-color-two/20", className)}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

type AccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Trigger
> & {
  icon?: React.ReactNode;
  hideIcon?: boolean;
  headerAsChild?: boolean;
};

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, icon, hideIcon, headerAsChild, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex" asChild={headerAsChild}>
    {headerAsChild ? (
      <div>
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>*>svg]:rotate-180",
            className
          )}
          {...props}
        >
          {children}
          {hideIcon ? null : icon ? (
            icon
          ) : (
            <SvgAnglesDown className="h-6 w-6 shrink-0 transition-transform duration-200" />
          )}
        </AccordionPrimitive.Trigger>
      </div>
    ) : (
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>*>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        {hideIcon ? null : icon ? (
          icon
        ) : (
          <SvgAnglesDown className="h-6 w-6 shrink-0 transition-transform duration-200" />
        )}
      </AccordionPrimitive.Trigger>
    )}
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

type AccordionContentProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Content
> & {
  wrapperClassName?: string;
};

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, wrapperClassName, ...props }, ref) => (
  <AccordionPrimitive.Content
    data-lenis-prevent
    ref={ref}
    className={cn(
      "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    )}
    {...props}
  >
    <div className={cn("pb-4 pt-0", wrapperClassName)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
